import { trimEnd } from 'lodash';
import { ROUTES } from 'utils/constants/routes';
import { getInAppBrowser } from 'lib/inAppBrowser';
import { FINAL_STATUS, ISSUE_CATEGORIES, SEVERITY, STEPS } from './events';
import { FAILURE_BUCKETS } from './failureBuckets';

export const SCOPES = {
    V2: 'v2',
    SHARED: 'shared',
};

const V2_STEPS = {
    [ROUTES.GOVT_ID_V2]: STEPS.INSTRUCTIONS,
    [ROUTES.GOVT_ID_V2_CAPTURE_CAMERA]: STEPS.CAMERA,
    [ROUTES.GOVT_ID_V2_CAPTURE_NEXT]: STEPS.BETWEEN_SIDES,
    [ROUTES.GOVT_ID_V2_PHOTO_CONFIRM]: STEPS.CONFIRM,
    [ROUTES.GOVT_ID_V2_CAPTURE_SELFIE]: STEPS.SELFIE_INSTRUCTIONS,
    [ROUTES.GOVT_ID_V2_CAPTURE_SELFIE_LIVENESS]: STEPS.SELFIE_CAMERA,
    [ROUTES.GOVT_ID_V2_RESULTS]: STEPS.RESULT,
};

const SHARED_SCREENS = {
    // v2's copies of shared error screens, reported under the shared screens' names.
    [ROUTES.GOVT_ID_V2_ERROR_SDK_FAILED]: { step: STEPS.ERROR, errorScreen: 'sdk_failed' },
    [ROUTES.GOVT_ID_V2_ERROR_CAMERA_FAILED]: { step: STEPS.ERROR, errorScreen: 'camera_failed' },
    [ROUTES.GOVT_ID_ID_TIMEOUT]: { step: STEPS.TIMEOUT },
    [ROUTES.GOVT_ID_ERROR_SELFIE_TIMEOUT]: { step: STEPS.TIMEOUT },
    [ROUTES.VERIFICATION_ATTEMPTS_EXHAUSTED]: {
        step: STEPS.ATTEMPTS_EXHAUSTED,
        finalStatus: FINAL_STATUS.DOCUMENT_FAILED,
    },
    [ROUTES.ID_FAILED]: {
        step: STEPS.ATTEMPTS_EXHAUSTED,
        finalStatus: FINAL_STATUS.DOCUMENT_FAILED,
    },
    [ROUTES.GOVT_ID_ERROR_COUNTRY_MISMATCH]: {
        step: STEPS.ERROR,
        finalStatus: FINAL_STATUS.NOT_ACCEPTED,
    },
    // The redirect here fires before the user is known, so the screen reports it.
    [ROUTES.GOVT_ID_ERROR_UNSUPPORTED_BROWSER]: {
        step: STEPS.ERROR,
        issue: {
            category: ISSUE_CATEGORIES.UNSUPPORTED_DEVICE,
            code: 'ui.unsupported_browser',
            severity: SEVERITY.BLOCKING,
            failure_bucket: FAILURE_BUCKETS.TECHNICAL,
        },
    },
    // Built when the screen shows, so it names the app the link was opened in.
    [ROUTES.GOVT_ID_ERROR_IN_APP_BROWSER]: {
        step: STEPS.ERROR,
        issue: () => ({
            category: ISSUE_CATEGORIES.UNSUPPORTED_DEVICE,
            code: 'ui.in_app_browser',
            severity: SEVERITY.BLOCKING,
            failure_bucket: FAILURE_BUCKETS.TECHNICAL,
            in_app_browser: getInAppBrowser(),
        }),
    },
};

const ERROR_PREFIX = `${ROUTES.GOVT_ID_ERROR}/`;

// `/govtid/error/selfie/capture/failed` → `selfie_capture_failed`, `/govtid/idTimeout` → `id_timeout`.
const toScreenKey = (path) =>
    path
        .replace(ERROR_PREFIX, '')
        .replace(/^\/(?:govtid|verification)\//, '')
        .replace(/([a-z])([A-Z])/g, '$1_$2')
        .replace(/[/-]/g, '_')
        .toLowerCase();

/**
 * The journey step a pathname shows. Only the result screen is terminal without a fixed final
 * status: it depends on how the selfie went.
 */
export const resolveRoute = (pathname = '') => {
    const path = pathname.length > 1 ? trimEnd(pathname, '/') : pathname;

    if (V2_STEPS[path]) {
        return {
            step: V2_STEPS[path],
            scope: SCOPES.V2,
            terminal: path === ROUTES.GOVT_ID_V2_RESULTS,
        };
    }

    const shared = SHARED_SCREENS[path];

    if (shared || path.startsWith(ERROR_PREFIX)) {
        return {
            step: shared?.step ?? STEPS.ERROR,
            scope: SCOPES.SHARED,
            errorScreen: shared?.errorScreen ?? toScreenKey(path),
            terminal: Boolean(shared?.finalStatus),
            finalStatus: shared?.finalStatus,
            issue: typeof shared?.issue === 'function' ? shared.issue() : shared?.issue,
        };
    }

    return { step: null, scope: null, terminal: false };
};
